import React from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { urlFor } from '@/lib/sanity';
import { format } from 'date-fns';

interface PostCardProps {
  post: {
    title: string;
    slug: { current: string };
    excerpt: string;
    featuredImage?: any;
    category?: { title: string; slug: { current: string } };
    publishedAt: string;
    readTime?: string;
  };
}

export default function PostCard({ post }: PostCardProps) {
  const publishDate = format(new Date(post.publishedAt), 'MMMM d, yyyy');

  return (
    <Link 
      href={`/blog/${post.slug.current}`}
      className="group flex flex-col h-full bg-white rounded-3xl border border-border overflow-hidden transition-all duration-300 hover:shadow-xl hover:shadow-charcoal/5 hover:-translate-y-1"
    >
      {/* Image Area */}
      <div className="relative aspect-[3/2] w-full overflow-hidden bg-deep-green">
        {post.featuredImage ? (
          <Image
            src={typeof post.featuredImage === 'string' ? post.featuredImage : urlFor(post.featuredImage).url()}
            alt={(typeof post.featuredImage === 'string' ? "" : post.featuredImage.alt) || post.title}
            fill
            sizes="(max-width: 640px) 95vw, (max-width: 1024px) 45vw, 400px"
            className="object-cover transition-transform duration-500 group-hover:scale-105"
          />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-deep-green to-warm-green flex items-center justify-center opacity-60">
            <span className="font-serif italic text-white/20 text-4xl">Laurel</span>
          </div>
        )}
      </div>


      {/* Content Area */}
      <div className="flex flex-col flex-1 p-8">
        {post.category && (
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-bloom-gold-dark mb-4">
            {post.category.title}
          </span>
        )}
        <h3 className="font-serif text-2xl font-normal text-charcoal mb-4 group-hover:text-deep-green transition-colors leading-snug">
          {post.title}
        </h3>
        <p className="font-sans text-sm text-muted-foreground line-clamp-3 mb-8 leading-relaxed">
          {post.excerpt}
        </p>

        
        <div className="mt-auto pt-6 border-t border-border/50 flex items-center justify-between">
          <span className="text-xs text-muted-foreground font-medium uppercase tracking-widest" suppressHydrationWarning>
            {publishDate}
          </span>
          {post.readTime && (
            <span className="text-xs text-muted-foreground font-medium uppercase tracking-widest">
              {post.readTime?.toString().toUpperCase().includes('READ') ? post.readTime : `${post.readTime} MIN READ`}
            </span>
          )}
        </div>

      </div>
    </Link>
  );
}
